import type { SeoCta, SeoInternalLink, SeoPage, SeoSection, SeoSitemapEntry } from "./seo-engine";
import { SEO_URL_PREFIX } from "./seo-engine";
import { translate } from "./i18n";
import { DEFAULT_LOCALE, type Locale } from "./locale";
import answerPagesSeed from "@/data/answer-pages-seed.json";

// ---------- answer-page intent registry ----------
//
// The public engine payload does NOT expose `intent` for answer pages, so we
// rely on the curated seed file to map slug → intent at build time. Using
// the seed file as the single source of truth keeps the renderer in sync
// with the engine writes performed by scripts/seed-answer-pages.mjs.

export type AnswerPageIntent =
  | "cost"
  | "roi"
  | "decision"
  | "legal"
  | "process"
  | "comparison"
  | "district"
  | "general";

interface AnswerPageSeedRow {
  slug: string;
  intent?: string;
}

const ANSWER_INTENT_BY_SLUG = new Map<string, AnswerPageIntent>();
for (const row of answerPagesSeed as AnswerPageSeedRow[]) {
  if (typeof row?.slug === "string" && typeof row?.intent === "string") {
    ANSWER_INTENT_BY_SLUG.set(row.slug, row.intent as AnswerPageIntent);
  }
}

export function getAnswerPageIntent(slug: string): AnswerPageIntent {
  return ANSWER_INTENT_BY_SLUG.get(slug) ?? "general";
}

// Hardcoded fallback set of bare district slugs (i.e. without the `riads-`
// prefix) seeded from a known prerender. The authoritative source is the
// sitemap delivered with the SSR payload — `registerKnownDistrictSlugs` will
// merge those in at hydration time and on every SeoPage render. The fallback
// keeps property pages CTA-aware even on the first paint before hydration
// (and on routes loaded directly without an SSR initial payload).
const DISTRICT_BARE_SLUGS = new Set<string>([
  "arset-ben-chebli",
  "arset-el-houta",
  "arset-ihiri",
  "assouel",
  "ben-saleh",
  "berrima",
  "jnan-ben-chagra",
  "kaat-ben-nahid",
  "kennaria",
  "ksibat-nhas",
  "mouassine",
  "moulay-lyazid",
  "rahba-kedima",
  "riad-zitoun",
  "rmila",
  "sidi-ben-slimane",
  "sidi-ben-slimane-el-jazouli",
  "sidi-bou-amar",
  "sidi-mansour",
  "sidi-mimoun",
]);

/** Merge a list of bare district slugs into the registry consulted by
 *  `districtPageSlug`. Safe to call multiple times — duplicates are ignored.
 *  Accepts both bare slugs (`berrima`) and the full page slug (`riads-berrima`).
 */
export function registerKnownDistrictSlugs(
  slugs: Iterable<string> | null | undefined,
): void {
  if (!slugs) return;
  for (const raw of slugs) {
    if (typeof raw !== "string") continue;
    const bare = raw.startsWith("riads-") ? raw.slice("riads-".length) : raw;
    const normalized = bare.trim().toLowerCase();
    if (normalized) DISTRICT_BARE_SLUGS.add(normalized);
  }
}

/** Convenience: register district slugs from a sitemap payload. */
export function registerDistrictSlugsFromSitemap(
  sitemap: SeoSitemapEntry[] | null | undefined,
): void {
  if (!sitemap) return;
  const bare: string[] = [];
  for (const entry of sitemap) {
    if (entry?.pageType === "district" && typeof entry.slug === "string") {
      bare.push(entry.slug);
    }
  }
  registerKnownDistrictSlugs(bare);
}

export function slugifyDistrict(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export function districtPageSlug(districtName: string): string | null {
  const bare = slugifyDistrict(districtName);
  if (!bare) return null;
  return DISTRICT_BARE_SLUGS.has(bare) ? `riads-${bare}` : null;
}

function findSection(page: SeoPage, type: string): SeoSection | undefined {
  return page.sections?.find((s) => s.type === type);
}

function pickStr(rec: Record<string, unknown> | null | undefined, ...keys: string[]): string | null {
  if (!rec) return null;
  for (const k of keys) {
    const v = rec[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

export function getPropertyDistrict(page: SeoPage): string | null {
  const market = (findSection(page, "market_context")?.data ?? null) as Record<string, unknown> | null;
  const raw = pickStr(market, "district");
  // Engine uses the literal sentinel "unclear" when it has no signal.
  if (!raw || raw.toLowerCase() === "unclear") return null;
  return raw;
}

const GENERIC_REPORT_CTA_RX = /request a property report/i;

/**
 * Returns the primary call-to-action for the top of an SEO page.
 *
 * - On property pages with a known district → district-aware browse CTA
 *   pointing to that district's `/r/riads-<slug>` page.
 * - On property pages without a known district → generic "Browse all
 *   riads for sale in Marrakech" pointing to /properties.
 * - On district pages whose engine CTA still points back to the contact
 *   form (the default "Browse district listings") → swap to a topical
 *   "Browse all riads for sale in Marrakech" pointing to /properties.
 * - Otherwise → return the engine's CTA unchanged.
 */
export function buildPrimarySeoCta(page: SeoPage, locale: Locale = DEFAULT_LOCALE): SeoCta | null {
  const t = (key: string, vars?: Record<string, string | number>) =>
    translate(locale, key, vars);

  if (page.pageType === "property_detail") {
    const district = getPropertyDistrict(page);
    if (district) {
      const slug = districtPageSlug(district);
      if (slug) {
        return {
          label: t("seo_cta.browse_district", { district }),
          href: `${SEO_URL_PREFIX}/${slug}`,
          type: "browse_listings",
        };
      }
    }
    return {
      label: t("seo_cta.browse_all"),
      href: "/properties",
      type: "browse_listings",
    };
  }

  const original = page.cta;
  // District pages whose engine CTA still routes back to the contact form
  // (the previous default for "Browse district listings") get swapped for a
  // topical browse. Match on destination, not label, so engine copy tweaks
  // don't silently bypass this override.
  if (
    page.pageType === "district" &&
    original &&
    typeof original.href === "string" &&
    original.href.startsWith("/contact")
  ) {
    return {
      label: t("seo_cta.browse_all"),
      href: "/properties",
      type: "browse_listings",
    };
  }

  // Answer pages: dispatch by curated intent. Engine ships a default
  // lead_capture CTA for these — we override the top-of-page CTA to push
  // readers into the live inventory (which is where the SEO traffic is
  // most likely to convert), while keeping the lead-capture box at the
  // bottom of the page intact via buildLeadFormCta.
  if (page.pageType === "answer") {
    const intent = getAnswerPageIntent(page.slug);
    switch (intent) {
      case "roi":
        // ROI questions are guesthouse-leaning; route to the guesthouse-
        // friendly browse path. /properties accepts arbitrary query strings
        // and ignores unknown ones, so this is safe even before any filter
        // UI honours `bestUse=guesthouse`.
        return {
          label: t("seo_cta.browse_guesthouse"),
          href: "/properties?bestUse=guesthouse",
          type: "browse_listings",
        };
      case "district":
      case "comparison":
      case "cost":
      case "decision":
      case "legal":
      case "process":
      case "general":
      default:
        return {
          label: t("seo_cta.browse_all"),
          href: "/properties",
          type: "browse_listings",
        };
    }
  }

  // For other page types, keep the engine's CTA but downgrade the generic
  // "Request a property report" lead-capture button on the top of the page
  // to a clearer browse action. The lead form box at the bottom keeps the
  // original lead-capture cta intact.
  if (original && GENERIC_REPORT_CTA_RX.test(original.label)) {
    return {
      label: t("seo_cta.browse_all"),
      href: "/properties",
      type: "browse_listings",
    };
  }

  return original ?? null;
}

/**
 * Returns the CTA used for the bottom lead-form box. We deliberately keep
 * the original engine CTA when it is `lead_capture` so the existing lead
 * collection path is preserved. For non-lead types we return null and the
 * box is not rendered.
 */
export function buildLeadFormCta(page: SeoPage): SeoCta | null {
  const original = page.cta;
  if (original?.type === "lead_capture") return original;
  return null;
}

// ---------------------------------------------------------------------------
// Derived internal links — generated from the page payload when the engine
// provides fewer than MIN_ENGINE_LINKS internal links. These supplement (not
// replace) engine-provided links so property pages always have contextual
// cross-links to the district page, strategy page, and key guides.
// ---------------------------------------------------------------------------

const MIN_ENGINE_LINKS = 2;

// Guides that are relevant to property pages regardless of specific signals.
const FALLBACK_GUIDE_SLUGS: Array<{ slug: string; label: string }> = [
  { slug: "cost-to-renovate-riad-marrakech", label: "Cost to renovate a riad in Marrakech" },
  { slug: "how-to-buy-a-riad-in-marrakech", label: "How to buy a riad — step-by-step guide" },
  { slug: "is-it-worth-buying-a-riad-in-marrakech", label: "Is buying a riad in Marrakech worth it?" },
];

// Strategy / opportunity slugs keyed by pricing signal.
const OPPORTUNITY_SLUG_BY_POSITIONING: Record<string, { slug: string; label: string }> = {
  underpriced: {
    slug: "underpriced-riads-marrakech",
    label: "All underpriced riads in Marrakech",
  },
  below_market: {
    slug: "underpriced-riads-marrakech",
    label: "All underpriced riads in Marrakech",
  },
  overpriced: {
    slug: "best-district-to-buy-a-riad-marrakech",
    label: "Best districts to buy a riad",
  },
  ambitious: {
    slug: "best-district-to-buy-a-riad-marrakech",
    label: "Best districts to buy a riad",
  },
};

/**
 * Build a supplementary list of internal links for a page, derived entirely
 * from the page payload. Intended for cases where the engine ships 0–1 links.
 *
 * Deduplicates against `existingLinks` so we never produce a duplicate URL.
 */
export function buildDerivedInternalLinks(
  page: SeoPage,
  existingLinks: SeoInternalLink[] = [],
): SeoInternalLink[] {
  if (page.pageType !== "property_detail") return [];
  if ((existingLinks?.length ?? 0) >= MIN_ENGINE_LINKS) return [];

  const existingUrls = new Set(
    existingLinks
      .map((l) => l.url ?? l.href ?? "")
      .filter(Boolean)
      .map((u) => u.replace(/^https?:\/\/[^/]+/, "")),
  );

  const derived: SeoInternalLink[] = [];

  const push = (url: string, label: string) => {
    const path = url.replace(/^https?:\/\/[^/]+/, "");
    if (!existingUrls.has(path)) {
      existingUrls.add(path);
      derived.push({ label, url });
    }
  };

  // 1. District page
  const district = getPropertyDistrict(page);
  if (district) {
    const slug = districtPageSlug(district);
    if (slug) push(`${SEO_URL_PREFIX}/${slug}`, `Riads for sale in ${district}`);
  }

  // 2. Opportunity page based on price positioning
  const pricingData = (findSection(page, "pricing")?.data ?? null) as Record<string, unknown> | null;
  const rawPos = pickStr(pricingData, "positioning", "pricePositioning");
  if (rawPos) {
    const key = rawPos.toLowerCase().trim();
    const opp = OPPORTUNITY_SLUG_BY_POSITIONING[key];
    if (opp) push(`${SEO_URL_PREFIX}/${opp.slug}`, opp.label);
  }

  // 3. Fallback guide links (up to 2 to keep the list tight)
  let guidesAdded = 0;
  for (const g of FALLBACK_GUIDE_SLUGS) {
    if (guidesAdded >= 2) break;
    push(`${SEO_URL_PREFIX}/${g.slug}`, g.label);
    guidesAdded++;
  }

  return derived;
}
